import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  FiChevronLeft,
  FiArrowUp,
  FiCheck,
  FiCamera,
  FiTrash2,
  FiX,
  FiSearch,
} from 'react-icons/fi';
import { Button, Dialog, DialogTitle, DialogActions } from '@mui/material';
import PlaylistCoverCrop from './PlaylistCoverCrop';
import useEditorFieldLayout from './useEditorFieldLayout';
import EditorSingleLineInput from './EditorSingleLineInput';
import useEditorTextScale from './useEditorTextScale';
import imageCompression from 'browser-image-compression';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { playlistApi } from '../../api/playlist';
import api from '../../api/api';
import PageLayout from '../../components/layout/PageLayout';
import { pageFrameStyle } from '../../components/layout/PageFrame';
import AppUiScope from '../../app-ui/AppUiScope';
import PlaylistArtwork from '../../components/content/PlaylistArtwork';
import { appendSeedAlbum } from './albumPlaylistSeed';
import {
  editorItemKey,
  orderedEditorItems,
  uniqueEditorItems,
  editorPayload,
  editorError,
  editorSnapshot,
  cancelEditorPath,
} from './playlistEditorModel';
import { invalidatePlaylistConsumers, removeDeletedPlaylist } from './playlistActionsModel';
import { removeRecent, recentScope } from '../../features/recent/recentContentStore';
import PlaylistEditorGuard, { EditorConfirm } from './PlaylistEditorGuard';
import PlaylistEditorItems from './PlaylistEditorItems';
import useEditorRecommendations from './useEditorRecommendations';
import { webPreviewPlayer } from '../../features/preview/previewPlayer';
import PlaylistDeleteDialog from './PlaylistDeleteDialog';
import '../review/UserReviewDetail.css';
import '../../features/browse/Browse.css';
import './PlaylistDetailSurface.css';
import './PlaylistEditor.css';
const emptyDraft = (seed) => ({
  type: seed ? 'ALBUM' : 'TRACK',
  title: '',
  description: '',
  externalLinkUrl: '',
  imageUrl: '',
  items: appendSeedAlbum([], seed),
});
function PlaylistFormContents({ mode, user }) {
  const location = useLocation(),
    navigate = useNavigate(),
    { id } = useParams(),
    client = useQueryClient(),
    { locale } = useWebLocale(),
    en = locale === 'en',
    edit = mode === 'edit';
  const seed =
    String(location.state?.seedUserId) === String(user?.id) ? location.state?.seedAlbum : null;
  const [draft, setDraft] = useState(() => emptyDraft(seed)),
    [loading, setLoading] = useState(edit),
    [loadError, setLoadError] = useState(false),
    [forbidden, setForbidden] = useState(false),
    [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(''),
    [failure, setFailure] = useState(''),
    [invalid, setInvalid] = useState(''),
    [confirm, setConfirm] = useState(null),
    [deleteOpen, setDeleteOpen] = useState(false),
    [deleteError, setDeleteError] = useState(false),
    [photoActions, setPhotoActions] = useState(false),
    [cropFile, setCropFile] = useState(null),
    [compactHeader, setCompactHeader] = useState(false);
  const [query, setQuery] = useState(''),
    [results, setResults] = useState(null),
    [searching, setSearching] = useState(false),
    [searchError, setSearchError] = useState(false),
    [searchRetry, setSearchRetry] = useState(0);
  const recommendations = useEditorRecommendations({
    items: draft.items,
    results,
    type: draft.type,
  });
  const previewOwner = `playlist-editor:${user?.id ?? 'guest'}:${location.key}`;
  useEffect(() => {
    webPreviewPlayer.setMiniPlayerSuppressed(previewOwner, draft.type === 'TRACK');
    if (draft.type !== 'TRACK') webPreviewPlayer.dismissOwned(previewOwner);
    return () => {
      webPreviewPlayer.dismissOwned(previewOwner);
      webPreviewPlayer.setMiniPlayerSuppressed(previewOwner, false);
    };
  }, [previewOwner, draft.type]);
  const alive = useRef(true),
    pending = useRef(false),
    baseline = useRef(editorSnapshot(emptyDraft())),
    initialLink = useRef(''),
    playlist = useRef(null),
    fileInput = useRef(null),
    toolbar = useRef(null),
    textProbe = useRef(null),
    inputs = useRef({}),
    guard = useRef({ dirty: false, busy: false, released: false });
  const textScale = useEditorTextScale(textProbe);
  useEditorFieldLayout(
    inputs,
    draft.description,
    Boolean(user) && !loading && !loadError && !forbidden,
    textScale,
  );
  useLayoutEffect(() => {
    const node = toolbar.current;
    if (!node) return;
    const measure = () => setCompactHeader(node.clientWidth < 340);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [user]);
  const dirty = editorSnapshot(draft) !== baseline.current;
  guard.current.dirty = dirty;
  guard.current.busy = Boolean(busy) || pending.current;
  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const active = () =>
    alive.current && user && String(useUserStore.getState().user?.id) === String(user.id);
  const assertActive = () => {
    if (!active()) throw Error('Inactive editor');
  };
  useEffect(() => {
    if (!user)
      navigate(`/login?redirectUrl=${encodeURIComponent(location.pathname + location.search)}`, {
        replace: true,
      });
  }, [user, navigate, location.pathname, location.search]);
  useEffect(() => {
    if (!edit || !user) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    setForbidden(false);
    playlistApi
      .getPlaylistById(id, {
        signal: controller.signal,
        optionalAuth: true,
        suppressGlobalError: true,
      })
      .then(({ data }) => {
        if (controller.signal.aborted || !active()) return;
        if (String(data.userId) !== String(user.id)) {
          setForbidden(true);
          return;
        }
        const raw = data.type === 'TRACK' ? data.tracks : data.albums;
        if ((raw || []).some((item) => !item.sourceId || !item.albumSource))
          throw Error('Incomplete source identity');
        const loaded = {
          type: data.type,
          title: data.title || '',
          description: data.description || '',
          externalLinkUrl: data.externalLinkUrl || '',
          imageUrl: data.imageUrl || '',
          items: orderedEditorItems(raw),
        };
        playlist.current = data;
        initialLink.current = loaded.externalLinkUrl;
        baseline.current = editorSnapshot(loaded);
        setDraft({
          ...loaded,
          items: loaded.type === 'ALBUM' ? appendSeedAlbum(loaded.items, seed) : loaded.items,
        });
        if (seed && loaded.type === 'TRACK')
          setFailure(
            en
              ? 'An album cannot be added to a track playlist.'
              : '곡 플리에는 앨범 전체를 추가할 수 없습니다.',
          );
      })
      .catch(() => {
        if (!controller.signal.aborted && active()) setLoadError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted && active()) setLoading(false);
      });
    return () => controller.abort();
    // A keyed editor captures the account, route and seed for this lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, id, reload]);
  useEffect(() => {
    const text = query.trim(),
      controller = new AbortController();
    setResults(null);
    setSearchError(false);
    setSearching(false);
    if (!text || !user) return () => controller.abort();
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.getPublic(
          `/search/${draft.type === 'TRACK' ? 'tracks' : 'albums'}?q=${encodeURIComponent(text)}&page=1&size=20`,
          { signal: controller.signal, suppressGlobalError: true },
        );
        const items = res.data?.data ?? res.data;
        if (!Array.isArray(items)) throw Error('Invalid search response');
        if (!controller.signal.aborted && active()) setResults(uniqueEditorItems(items));
      } catch {
        if (!controller.signal.aborted && active()) setSearchError(true);
      } finally {
        if (!controller.signal.aborted && active()) setSearching(false);
      }
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, draft.type, searchRetry]);
  const update = (field, value) => {
    if (pending.current) return;
    setDraft((d) => ({ ...d, [field]: value }));
    if (invalid === field) setInvalid('');
  };
  const setItems = (value) => {
    if (pending.current) return;
    setDraft((d) => ({ ...d, items: typeof value === 'function' ? value(d.items) : value }));
    setInvalid('');
  };
  const hasDetailReturn =
    edit &&
    String(location.state?.playlistReturn?.id) === String(id) &&
    /^\/lists\/(?!create|new)[^/]+(?:\/comment\/\d+)?(?:\?.*)?$/.test(
      location.state?.playlistReturn?.path || '',
    );
  const leave = () => {
    guard.current.released = true;
    if (hasDetailReturn && window.history.state?.idx > 0) navigate(-1);
    else navigate(cancelEditorPath(mode, id, location.search), { replace: true });
  };
  const cancel = () => {
    if (pending.current) return;
    if (dirty) setConfirm({ kind: 'leave' });
    else leave();
  };
  const changeType = (type) => {
    if (edit || pending.current || draft.type === type) return;
    if (draft.items.length) setConfirm({ kind: 'type', type });
    else applyType(type);
  };
  const applyType = (type) => {
    update('type', type);
    setItems([]);
    setQuery('');
    setConfirm(null);
  };
  const choosePhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || pending.current || !active()) return;
    if (!file.type.startsWith('image/')) {
      setFailure(en ? 'Only image files can be used.' : '이미지 파일만 사용할 수 있습니다.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFailure(en ? 'Choose an image no larger than 5 MB.' : '이미지는 5MB 이하로 선택해주세요.');
      return;
    }
    setFailure('');
    setCropFile(file);
  };
  const upload = async (file) => {
    if (!file || pending.current || !active()) return;
    setCropFile(null);
    pending.current = true;
    guard.current.busy = true;
    setBusy('upload');
    setFailure('');
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
        initialQuality: 0.82,
      });
      assertActive();
      if (compressed.size > 5 * 1024 * 1024) throw Error('Image too large');
      const body = new FormData();
      body.append('image', compressed);
      const response = await api.postAuth('/images/upload', body, { suppressGlobalError: true });
      assertActive();
      const url = response.data?.data?.url ?? response.data?.url;
      if (!url) throw Error('Missing image URL');
      setDraft((d) => ({ ...d, imageUrl: url }));
    } catch {
      if (active())
        setFailure(
          en
            ? 'Image upload failed. Your previous cover is kept.'
            : '이미지를 업로드하지 못했어요. 이전 커버는 유지됩니다.',
        );
    } finally {
      if (active()) {
        pending.current = false;
        guard.current.busy = false;
        setBusy('');
      }
    }
  };
  const submit = async (event) => {
    event?.preventDefault();
    if (pending.current || !active() || loading || loadError || forbidden) return;
    const error = editorError(draft);
    setInvalid(error || '');
    if (error) {
      inputs.current[error]?.focus();
      return;
    }
    pending.current = true;
    guard.current.busy = true;
    setBusy('save');
    setFailure('');
    try {
      const payload = editorPayload(draft, mode, initialLink.current),
        config = { suppressGlobalError: true };
      const response = edit
        ? await playlistApi.updatePlaylist(id, payload, config)
        : await playlistApi.createPlaylist(payload, config);
      assertActive();
      baseline.current = editorSnapshot(draft);
      guard.current.released = true;
      void invalidatePlaylistConsumers(client);
      // Back remounts detail and fetches fresh data while preserving its history/scroll key.
      if (hasDetailReturn && window.history.state?.idx > 0) navigate(-1);
      else {
        const target = response?.data?.slug ?? response?.data?.id ?? (edit ? id : null);
        navigate(target ? `/lists/${encodeURIComponent(target)}` : '/lists', { replace: true });
      }
    } catch (error) {
      if (active())
        setFailure(
          /(?:Create|Update)PlayListDTO/.test(error?.message || '')
            ? en
              ? 'This server does not support external links yet. Your draft is kept.'
              : '현재 서버가 외부 링크 저장을 지원하지 않습니다. 작성 내용은 유지됩니다.'
            : en
              ? 'Could not save. Your draft is kept. Try again.'
              : '저장하지 못했어요. 작성 내용은 유지됩니다. 다시 시도해 주세요.',
        );
    } finally {
      if (active()) {
        pending.current = false;
        guard.current.busy = false;
        setBusy('');
      }
    }
  };
  const remove = async () => {
    if (
      pending.current ||
      !active() ||
      !playlist.current ||
      String(playlist.current.userId) !== String(user.id)
    )
      return;
    pending.current = true;
    guard.current.busy = true;
    setBusy('delete');
    setDeleteError(false);
    try {
      await playlistApi.deletePlaylist(id, { suppressGlobalError: true });
      assertActive();
      removeRecent(
        recentScope(user.id),
        (x) => x.id === `playlist:${id}` || x.id === `playlist:${playlist.current.slug}`,
      );
      removeDeletedPlaylist(client, id);
      void invalidatePlaylistConsumers(client);
      guard.current.released = true;
      if (hasDetailReturn && window.history.state?.idx > 1) navigate(-2);
      else navigate('/lists', { replace: true });
    } catch {
      if (active()) setDeleteError(true);
    } finally {
      if (active()) {
        pending.current = false;
        guard.current.busy = false;
        setBusy('');
      }
    }
  };
  const title = edit
    ? en
      ? 'Edit Playlist'
      : '플리 수정'
    : en
      ? 'Create Playlist'
      : '플리 만들기';
  const saveLabel =
    busy === 'save'
      ? edit
        ? en
          ? 'Updating playlist…'
          : '플리 수정 중'
        : en
          ? 'Creating playlist…'
          : '플리 만드는 중'
      : edit
        ? en
          ? 'Save Playlist Changes'
          : '플리 수정 완료'
        : en
          ? 'Create Playlist'
          : '플리 만들기';
  const validation = {
    title: en ? 'Enter a title (up to 60 characters).' : '제목을 입력해 주세요. (최대 60자)',
    description: en ? 'Use at most 5,000 characters.' : '설명은 최대 5000자입니다.',
    externalLinkUrl: en
      ? 'Enter a valid http:// or https:// link.'
      : 'http:// 또는 https://로 시작하는 올바른 링크를 입력해 주세요.',
    items:
      draft.type === 'TRACK'
        ? en
          ? 'Add at least one track.'
          : '곡을 하나 이상 추가해 주세요.'
        : en
          ? 'Add at least one album.'
          : '앨범을 하나 이상 추가해 주세요.',
  };
  const field = (name, label, placeholder, maxLength, textarea = false) => (
    <div className="pp-editor-field">
      <label htmlFor={`editor-${name}`}>
        {label}
        {maxLength && name !== 'externalLinkUrl' && (
          <span>
            {draft[name].length}/{maxLength}
          </span>
        )}
      </label>
      {React.createElement(textarea ? 'textarea' : EditorSingleLineInput, {
        id: `editor-${name}`,
        name,
        ref: (el) => {
          inputs.current[name] = el;
        },
        value: draft[name],
        maxLength,
        placeholder,
        rows: textarea ? 3 : undefined,
        ...(textarea ? {} : { focusAtEnd: name === 'title' }),
        inputMode: name === 'externalLinkUrl' ? 'url' : undefined,
        autoCapitalize: name === 'externalLinkUrl' ? 'none' : undefined,
        spellCheck: name === 'externalLinkUrl' ? false : undefined,
        'aria-invalid': invalid === name,
        'aria-describedby': invalid === name ? 'editor-validation' : undefined,
        onChange: (e) => update(name, e.target.value),
      })}
    </div>
  );
  if (!user)
    return <p role="status">{en ? 'Log in to continue.' : '로그인 후 이용할 수 있습니다.'}</p>;
  return (
    <AppUiScope
      surface="lists"
      className={`pp-parity pp-browse pp-playlist-surface pp-playlist-editor${textScale >= 1.4 ? ' is-expanded-editor-text' : ''}`}
    >
      <span ref={textProbe} className="pp-editor-text-probe" aria-hidden="true" />
      <PlaylistEditorGuard state={guard} en={en} />
      <div
        ref={toolbar}
        className={`pp-playlist-toolbar${compactHeader ? ' is-compact-editor' : ''}`}
      >
        <div className="pp-page-frame" style={pageFrameStyle}>
          <button
            type="button"
            disabled={Boolean(busy)}
            aria-label={en ? 'Back' : '뒤로가기'}
            onClick={cancel}
          >
            <FiChevronLeft size={23} />
          </button>
          <span>{title}</span>
          <div className="pp-editor-header-actions">
            {edit && (
              <button
                type="button"
                className="pp-editor-header-delete"
                disabled={Boolean(busy) || loading || loadError || forbidden}
                aria-label={
                  busy === 'delete'
                    ? en
                      ? 'Deleting playlist…'
                      : '플리 삭제 중'
                    : en
                      ? 'Delete Playlist'
                      : '플리 삭제'
                }
                onClick={() => {
                  setDeleteError(false);
                  setDeleteOpen(true);
                }}
              >
                <FiTrash2 size={21} />
              </button>
            )}
            <button
              type="button"
              className="pp-editor-header-submit"
              disabled={Boolean(busy) || loading || loadError || forbidden}
              aria-label={saveLabel}
              onClick={submit}
            >
              {busy === 'save' ? (
                <span className="pp-editor-resolving" />
              ) : edit ? (
                <FiCheck size={23} />
              ) : (
                <FiArrowUp size={23} />
              )}
            </button>
          </div>
        </div>
      </div>
      <PageLayout paddingTop={compactHeader ? '88px' : '52px'}>
        <h1 className="pp-editor-sr-only">{title}</h1>
        {loading ? (
          <p role="status">{en ? 'Loading playlist…' : '플리를 불러오는 중…'}</p>
        ) : loadError ? (
          <div role="alert">
            <p>{en ? 'Could not load this playlist.' : '플리를 불러오지 못했어요.'}</p>
            <button onClick={() => setReload((x) => x + 1)}>{en ? 'Retry' : '다시 시도'}</button>
          </div>
        ) : forbidden ? (
          <p role="alert">
            {en ? 'Only the playlist owner can edit it.' : '플리 작성자만 수정할 수 있습니다.'}
          </p>
        ) : (
          <form onSubmit={(event) => event.preventDefault()} noValidate>
            <fieldset disabled={Boolean(busy)} className="pp-editor-fields">
              <div className="pp-editor-field">
                <span className="pp-editor-label">{en ? 'Composition' : '구성 방식'} *</span>
                <div className="pp-editor-segments">
                  {['TRACK', 'ALBUM'].map((type) => (
                    <button
                      type="button"
                      key={type}
                      disabled={edit || Boolean(busy)}
                      aria-pressed={draft.type === type}
                      onClick={() => changeType(type)}
                    >
                      {type === 'TRACK'
                        ? en
                          ? 'Make with tracks'
                          : '곡으로 만들기'
                        : en
                          ? 'Make with albums'
                          : '앨범으로 만들기'}
                    </button>
                  ))}
                </div>
                {edit && (
                  <p className="pp-editor-note">
                    {en
                      ? 'Composition cannot be changed after creation.'
                      : '구성 방식은 만든 뒤 변경할 수 없습니다.'}
                  </p>
                )}
              </div>
              <div className="pp-editor-hero">
                <div className="pp-editor-cover-field">
                  <span className="pp-editor-label">{en ? 'Cover' : '커버'}</span>
                  <button
                    type="button"
                    className="pp-editor-cover"
                    aria-label={en ? 'Choose cover image' : '대표 이미지 선택'}
                    onClick={() => setPhotoActions(true)}
                  >
                    <PlaylistArtwork
                      playlist={{
                        imageUrl: draft.imageUrl,
                        previewImageUrls: draft.items.map((x) => x.albumImageUrl || x.imageUrl),
                      }}
                    />
                    <span className="pp-editor-camera">
                      <FiCamera size={18} />
                    </span>
                  </button>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    className="pp-editor-sr-only"
                    aria-label={en ? 'Cover image file' : '대표 이미지 파일'}
                    onChange={choosePhoto}
                  />
                  <p className="pp-editor-cover-hint">
                    {draft.imageUrl
                      ? en
                        ? 'Tap to change or delete'
                        : '눌러서 변경·삭제'
                      : en
                        ? 'Tap to select photo'
                        : '눌러서 사진 선택'}
                  </p>
                </div>
                <div className="pp-editor-hero-copy">
                  {field(
                    'title',
                    en ? 'Title *' : '제목 *',
                    en ? 'Enter a title.' : '제목을 입력하세요.',
                    60,
                  )}
                  <p className="pp-editor-note">
                    {draft.items.length}
                    {draft.type === 'TRACK'
                      ? en
                        ? ' tracks'
                        : '곡'
                      : en
                        ? ' albums'
                        : '장'} · {en ? 'Square image, up to 5 MB' : '정사각형 이미지 최대 5MB'}
                  </p>
                </div>
              </div>
              {field(
                'description',
                en ? 'Description' : '설명',
                en ? 'Describe your playlist.' : '플리에 대한 설명을 입력하세요.',
                5000,
                true,
              )}
              <div className="pp-editor-external">
                {field(
                  'externalLinkUrl',
                  en ? 'External link' : '외부 링크',
                  en ? 'Playlist link on another music service' : '다른 음악 서비스의 플리 링크',
                  2048,
                )}
                {draft.externalLinkUrl && (
                  <button
                    type="button"
                    className="pp-editor-clear"
                    aria-label={en ? 'Clear external link' : '외부 링크 모두 지우기'}
                    onClick={() => {
                      update('externalLinkUrl', '');
                      inputs.current.externalLinkUrl?.focus();
                    }}
                  >
                    <FiX size={18} />
                  </button>
                )}
                <p className="pp-editor-note">
                  {en
                    ? 'Connect a playlist made on another music service.'
                    : '다른 음악 서비스에서 만든 플리를 연결할 수 있어요.'}
                </p>
              </div>
              <section className="pp-editor-field">
                <label htmlFor="editor-search">
                  {draft.type === 'TRACK'
                    ? en
                      ? 'Add tracks'
                      : '곡 추가'
                    : en
                      ? 'Add albums'
                      : '앨범 추가'}{' '}
                  *
                </label>
                <div className="pp-editor-search-wrap">
                  <FiSearch size={18} aria-hidden="true" />
                  <EditorSingleLineInput
                    autoCapitalize="none"
                    spellCheck={false}
                    id="editor-search"
                    ref={(el) => {
                      inputs.current.items = el;
                    }}
                    value={query}
                    placeholder={
                      draft.type === 'TRACK'
                        ? en
                          ? 'Search track or artist'
                          : '곡 제목 또는 아티스트 검색'
                        : en
                          ? 'Search album or artist'
                          : '앨범 또는 아티스트 검색'
                    }
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {query && (
                    <button
                      type="button"
                      aria-label={en ? 'Clear search' : '검색어 지우기'}
                      onClick={() => {
                        setQuery('');
                        inputs.current.items?.focus();
                      }}
                    >
                      <FiX size={18} />
                    </button>
                  )}
                </div>
                {searching && <p role="status">{en ? 'Searching…' : '검색 중…'}</p>}
                {searchError && (
                  <div role="alert">
                    {en ? 'Could not load search results.' : '검색 결과를 불러오지 못했어요.'}
                    <button type="button" onClick={() => setSearchRetry((x) => x + 1)}>
                      {en ? 'Retry' : '다시 시도'}
                    </button>
                  </div>
                )}
                <PlaylistEditorItems
                  items={draft.items}
                  {...{ setItems, results, en, recommendations, previewOwner }}
                  type={draft.type}
                  busy={Boolean(busy)}
                  onAdd={(item, depth = 0) => {
                    if (pending.current) return;
                    setItems((old) =>
                      old.some((x) => editorItemKey(x) === editorItemKey(item))
                        ? old
                        : [...old, item],
                    );
                    void recommendations.load(item, depth);
                  }}
                />
              </section>
              {invalid && (
                <p id="editor-validation" role="alert">
                  {validation[invalid]}
                </p>
              )}
              {failure && <p role="alert">{failure}</p>}
              {busy === 'upload' && (
                <p role="status">{en ? 'Uploading image…' : '이미지를 업로드하는 중…'}</p>
              )}
              <div className="pp-editor-footer">
                <button type="button" onClick={cancel}>
                  {en ? 'Cancel' : '취소'}
                </button>
                <button type="button" onClick={submit} className="pp-editor-primary">
                  {saveLabel}
                </button>
              </div>
            </fieldset>
          </form>
        )}
      </PageLayout>
      {photoActions && (
        <Dialog
          open
          fullWidth
          maxWidth="xs"
          className="pp-review-dialog pp-editor-photo-actions"
          aria-labelledby="pp-photo-actions-title"
          onClose={() => setPhotoActions(false)}
        >
          <DialogTitle id="pp-photo-actions-title">
            {en ? 'Cover Image' : '대표 이미지'}
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={() => {
                setPhotoActions(false);
                fileInput.current?.click();
              }}
            >
              {draft.imageUrl
                ? en
                  ? 'Change Photo'
                  : '사진 변경'
                : en
                  ? 'Choose Photo'
                  : '사진 선택'}
            </Button>
            {draft.imageUrl && (
              <Button
                color="error"
                onClick={() => {
                  update('imageUrl', '');
                  setPhotoActions(false);
                }}
              >
                {en ? 'Delete Photo' : '사진 삭제'}
              </Button>
            )}
            <Button onClick={() => setPhotoActions(false)}>{en ? 'Cancel' : '취소'}</Button>
          </DialogActions>
        </Dialog>
      )}
      {cropFile && (
        <PlaylistCoverCrop
          key={cropFile.name + cropFile.lastModified}
          file={cropFile}
          en={en}
          onCancel={() => setCropFile(null)}
          onUse={upload}
        />
      )}
      {confirm && (
        <EditorConfirm
          en={en}
          title={
            confirm.kind === 'type'
              ? en
                ? 'Change Playlist Type'
                : '유형 변경'
              : en
                ? 'Cancel editing?'
                : '편집을 취소할까요?'
          }
          message={
            confirm.kind === 'type'
              ? en
                ? 'Changing the type will remove all selected items.'
                : '유형을 바꾸면 선택한 항목이 모두 제거됩니다.'
              : en
                ? 'Unsaved changes will be lost.'
                : '저장하지 않은 변경 내용은 사라집니다.'
          }
          confirm={confirm.kind === 'type' ? (en ? 'Change' : '변경') : undefined}
          onCancel={() => setConfirm(null)}
          onConfirm={() => (confirm.kind === 'type' ? applyType(confirm.type) : leave())}
        />
      )}
      {deleteOpen && (
        <PlaylistDeleteDialog
          en={en}
          busy={busy === 'delete'}
          error={deleteError}
          onClose={() => {
            if (!pending.current) setDeleteOpen(false);
          }}
          onDelete={remove}
        />
      )}
    </AppUiScope>
  );
}
export default function PlaylistForm({ mode = 'create' }) {
  const user = useUserStore((s) => s.user),
    location = useLocation();
  return (
    <PlaylistFormContents key={`${user?.id ?? 'guest'}:${location.key}`} user={user} mode={mode} />
  );
}
